/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useState } from 'react';

/* Import components */
import KpiCarouselApi2LastValue from '@/components/api/KpiCarouselApi2LastValue';
import KpiValueApi2LastValue from '@/components/api/KpiValueApi2LastValue';
import CodeBlock from '@/components/code/CodeBlock';
import LayoutSectionHeader from '@/components/tabs/layouts/LayoutSectionHeader';

/* Import configuration */
import {
    KPI_CAROUSEL_API2_LAST_VALUE_EXAMPLES,
    KPI_VALUE_API2_LAST_VALUE_EXAMPLES,
} from '@/components/tabs/layouts/layoutExamples';

interface KpiApi2LastValueLayoutsTabProps {
    theme: 'light' | 'dark';
}

function KpiApi2LastValueLayoutsTab({ theme }: KpiApi2LastValueLayoutsTabProps) {
    const [selectedKpiValueExampleIndex, setSelectedKpiValueExampleIndex] = useState(0);
    const [selectedKpiCarouselExampleIndex, setSelectedKpiCarouselExampleIndex] = useState(0);
    const selectedKpiValueExample = KPI_VALUE_API2_LAST_VALUE_EXAMPLES[selectedKpiValueExampleIndex];
    const selectedKpiCarouselExample = KPI_CAROUSEL_API2_LAST_VALUE_EXAMPLES[selectedKpiCarouselExampleIndex];

    return (
        <div className="layouts-tab">
            <LayoutSectionHeader sectionKey="kpi-api2-last-value" />

            <section className="layouts-section">
                <h3>KpiValueApi2LastValue</h3>
                <p>Loads the latest API2 value directly without requesting and aggregating a time series.</p>
                <div className="inline-flex flex-wrap items-center gap-3">
                    <label className="text-sm font-semibold" htmlFor="kpi-value-api2-last-value-example">
                        API value example
                    </label>
                    <select
                        id="kpi-value-api2-last-value-example"
                        className="select select-bordered w-auto"
                        value={selectedKpiValueExampleIndex}
                        onChange={(event) => {
                            setSelectedKpiValueExampleIndex(Number(event.target.value));
                        }}
                    >
                        {KPI_VALUE_API2_LAST_VALUE_EXAMPLES.map((example, index) => (
                            <option key={example.label} value={index}>{example.label}</option>
                        ))}
                    </select>
                </div>
                <h4>Code example</h4>
                <CodeBlock code={selectedKpiValueExample.code} theme={theme} />
                <h4>Rendered preview</h4>
                <section className="dashboard-panel layouts-demo-panel layouts-demo-panel--value">
                    <KpiValueApi2LastValue
                        calc={selectedKpiValueExample.props.calc}
                        calcCode={selectedKpiValueExample.props.calcCode}
                        conversion={selectedKpiValueExample.props.conversion}
                        date={selectedKpiValueExample.props.date}
                        fractionDigits={selectedKpiValueExample.props.fractionDigits}
                        info={selectedKpiValueExample.props.info}
                        series={selectedKpiValueExample.props.series}
                        showDate={selectedKpiValueExample.props.showDate}
                        showLegend={selectedKpiValueExample.props.showLegend}
                        title={selectedKpiValueExample.props.title}
                        trafficLight={selectedKpiValueExample.props.trafficLight}
                        unit={selectedKpiValueExample.props.unit}
                        urn={selectedKpiValueExample.props.urn}
                        valueLabels={selectedKpiValueExample.props.valueLabels}
                    />
                </section>
            </section>

            <section className="layouts-section">
                <h3>KpiCarouselApi2LastValue</h3>
                <p>Loads multiple latest API2 values directly and presents them in a shared carousel.</p>
                <div className="inline-flex flex-wrap items-center gap-3">
                    <label className="text-sm font-semibold" htmlFor="kpi-carousel-api2-last-value-example">
                        API carousel example
                    </label>
                    <select
                        id="kpi-carousel-api2-last-value-example"
                        className="select select-bordered w-auto"
                        value={selectedKpiCarouselExampleIndex}
                        onChange={(event) => {
                            setSelectedKpiCarouselExampleIndex(Number(event.target.value));
                        }}
                    >
                        {KPI_CAROUSEL_API2_LAST_VALUE_EXAMPLES.map((example, index) => (
                            <option key={example.label} value={index}>{example.label}</option>
                        ))}
                    </select>
                </div>
                <h4>Code example</h4>
                <CodeBlock code={selectedKpiCarouselExample.code} theme={theme} />
                <h4>Rendered preview</h4>
                <section className="dashboard-panel layouts-demo-panel layouts-demo-panel--value">
                    <KpiCarouselApi2LastValue
                        autoAdvanceMs={selectedKpiCarouselExample.props.autoAdvanceMs}
                        date={selectedKpiCarouselExample.props.date}
                        info={selectedKpiCarouselExample.props.info}
                        items={selectedKpiCarouselExample.props.items}
                        showDate={selectedKpiCarouselExample.props.showDate}
                        showLegend={selectedKpiCarouselExample.props.showLegend}
                    />
                </section>
            </section>
        </div>
    );
}

export default KpiApi2LastValueLayoutsTab;
